import React, { useMemo, useEffect, useState } from "react";
import { Box, Typography } from "@material-ui/core";
import { useSelectedTheme } from "../../../../../../../../Hooks";

// styles
import useStyles from "./styles";

const NewGaugeChart = ({
  minPrice,
  maxPrice,
  medianPrice,
  psiPrice,
  activeData,
  medianPricePerSqft,
  marketComparison,
}) => {
  const styles = useStyles();
  const {
    theme: { palette },
  } = useSelectedTheme();

  const calculatedMedianPrice = useMemo(() => {
    if (!activeData?.builtup_area_sqft || !medianPricePerSqft)
      return medianPrice;
    return medianPricePerSqft * activeData.builtup_area_sqft;
  }, [medianPricePerSqft, activeData?.builtup_area_sqft, medianPrice]);

  const calculateNeedlePosition = () => {
    if (!psiPrice || !calculatedMedianPrice) return 90;

    const threshold10Percent = calculatedMedianPrice * 0.1;
    const upperThreshold10 = calculatedMedianPrice + threshold10Percent;
    const lowerThreshold10 = calculatedMedianPrice - threshold10Percent;

    if (psiPrice === calculatedMedianPrice) {
      return 90;
    } else if (
      psiPrice > calculatedMedianPrice &&
      psiPrice < upperThreshold10
    ) {
      const ratio = (psiPrice - calculatedMedianPrice) / threshold10Percent;
      return 90 - ratio * 30;
    } else if (
      psiPrice < calculatedMedianPrice &&
      psiPrice > lowerThreshold10
    ) {
      const ratio = (calculatedMedianPrice - psiPrice) / threshold10Percent;
      return 90 + ratio * 30;
    } else if (psiPrice > upperThreshold10) {
      const maxRange = Math.max(maxPrice, calculatedMedianPrice * 2);
      const ratio = Math.min(
        1,
        (psiPrice - upperThreshold10) / (maxRange - upperThreshold10)
      );
      return 60 - ratio * 60;
    } else if (psiPrice <= lowerThreshold10) {
      const minRange = Math.min(minPrice, calculatedMedianPrice * 0.5);
      const ratio = Math.min(
        1,
        (lowerThreshold10 - psiPrice) / (lowerThreshold10 - minRange)
      );
      return 120 + ratio * 60;
    }

    return 90;
  };

  const getColors = () => {
    if (!psiPrice || !calculatedMedianPrice)
      return { needleColor: "#EAECF0", textColor: "#667085" };

    const needlePos = calculateNeedlePosition();
    if (needlePos >= 135 && needlePos <= 180) {
      return { needleColor: "#66C61C", textColor: "#027A48" };
    } else if (needlePos >= 75 && needlePos < 135) {
      return { needleColor: "#FDB022", textColor: "#B54708" };
    } else if (needlePos >= 0 && needlePos < 75) {
      return { needleColor: "#D92D20", textColor: "#B42318" };
    } else if (needlePos === 90) {
      return { needleColor: "#7232A1", textColor: "#7232A1" };
    }
    return { needleColor: "#EAECF0", textColor: "#667085" };
  };

  const [needlePosition, setNeedlePosition] = useState(90);
  const [targetNeedlePosition, setTargetNeedlePosition] = useState(90);
  const [colorData, setColorData] = useState({
    needleColor: "#EAECF0",
    textColor: "#667085",
  });


  useEffect(() => {
    const newTarget = calculateNeedlePosition();
    setTargetNeedlePosition(newTarget);
    setColorData(getColors());
  }, [
    psiPrice,
    minPrice,
    maxPrice,
    activeData,
    medianPricePerSqft,
    marketComparison,
  ]);

  useEffect(() => {
    if (needlePosition === targetNeedlePosition) return;
    let animationFrame;
    const animate = () => {
      const diff = targetNeedlePosition - needlePosition;
      if (Math.abs(diff) < 0.5) {
        setNeedlePosition(targetNeedlePosition);
        return;
      }
      setNeedlePosition((prev) => prev + diff * 0.05);
      animationFrame = requestAnimationFrame(animate);
    };
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [targetNeedlePosition, needlePosition]);

  const formatNumber = (number) => {
    if (!number) return "";
    if (Math.abs(number) >= 1000000) {
      const value = number / 1000000;
      return `${parseFloat(value.toFixed(2))}M`;
    }
    return number.toLocaleString(undefined, {
      minimumFractionDigits: number % 1 !== 0 ? 2 : 0,
      maximumFractionDigits: 2,
    });
  };
  const formatSize = (value, unit = "sqft") => {
    if (!value) return "";
    if (unit === "sqm" && value < 1000) {
      return `${value % 1 !== 0 ? value.toFixed(2) : value} ${unit}`;
    }
    return `${value.toLocaleString(undefined, {
      minimumFractionDigits: value % 1 !== 0 ? 2 : 0,
      maximumFractionDigits: 2,
    })} ${unit}`;
  };

  const { needleColor, textColor } = colorData;

  return (
    <Box className={styles.gaugeContainer}>
      <Typography className={styles.mpiTitle}>MPI</Typography>
      <Box className={styles.gaugeWrapper}>
        <Box className={styles.gaugeSection}>
          <Box className={styles.centerTextContainer}>
            <Typography className={styles.marketPriceLabel}>
              Assumed market place
            </Typography>
            <Box className={styles.marketPriceValueContainer}>
              <Typography className={styles.marketPriceValue}>
                {formatNumber(calculatedMedianPrice)}
              </Typography>
              <Typography className={styles.currencyLabel}>AED</Typography>
            </Box>
            {activeData?.builtup_area_sqft && (
              <Typography className={styles.medianSize}>
                Median Size: {formatSize(activeData.builtup_area_sqft, "sqft")}
              </Typography>
            )}
            {activeData?.builtup_area_sqm && (
              <Typography className={styles.medianSize}>
                Median Size: {formatSize(activeData.builtup_area_sqm, "sqm")}
              </Typography>
            )}
          </Box>

          <svg
            className={styles.gaugeSvg}
            viewBox="0 0 300 160"
            preserveAspectRatio="xMidYMid meet"
          >
            <path
              d="M30,150 A120,120 0 0,1 90,66.5"
              fill="none"
              stroke="#66C61C"
              strokeWidth="24"
              strokeLinecap="round"
            />

            <path
              d="M90,66.5 A120,120 0 0,1 210,66.5"
              fill="none"
              stroke="#FDB022"
              strokeWidth="24"
              strokeLinecap="round"
            />

            <path
              d="M210,66.5 A120,120 0 0,1 270,150"
              fill="none"
              stroke="#D92D20"
              strokeWidth="24"
              strokeLinecap="round"
            />

            <line
              x1="150"
              y1="61"
              x2="150"
              y2="40"
              stroke="#7232A1"
              strokeWidth="5"
              strokeLinecap="round"
            />

            {/* Needle */}
            {(() => {
              const centerX = 150;
              const centerY = 150;
              const needleLength = 90;
              const labelOffset = 60;
              const angleRad = (needlePosition * Math.PI) / 180;
              const tipX = centerX + needleLength * Math.cos(angleRad);
              const tipY = centerY - needleLength * Math.sin(angleRad);
              const labelX =
                centerX + (needleLength + labelOffset) * Math.cos(angleRad);
              const labelY =
                centerY - (needleLength + labelOffset) * Math.sin(angleRad);
              return (
                <g>
                  <line
                    x1={centerX}
                    y1={centerY}
                    x2={tipX}
                    y2={tipY}
                    stroke={needleColor}
                    strokeWidth="4"
                    strokeLinecap="round"
                  />
                  <circle cx={centerX} cy={centerY} r="8" fill={needleColor} />
                  <text
                    x={labelX}
                    y={labelY}
                    textAnchor="middle"
                    fontSize="12"
                    fontWeight="bold"
                    fill={palette.text.primary}
                    style={{ userSelect: "none" }}
                    alignmentBaseline="middle"
                  >
                    {formatNumber(psiPrice)}
                  </text>
                </g>
              );
            })()}
          </svg>
        </Box>
      </Box>
    </Box>
  );
};

export default NewGaugeChart;